'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { useTheme } from '@loci/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useCitySurface } from '@/lib/city-context.client';
import {
  getSystemPrefersDark,
  getThemePreference,
  getThemePreferenceSnapshot,
  applyAccountThemePreference,
  hydrateThemePreferenceFromCookie,
  resolveThemeMode,
  subscribeThemePreference,
} from '@/lib/theme/themePreference';

export interface ThemePreferenceSyncProps {
  /** WEB-693 forced-dark surfaces (marketing-city + `/admin`). */
  forcedDark?: boolean;
}

/**
 * WEB-694 — keep the resolved theme in sync with the stored preference for
 * the lifetime of the tab. Settings pages write the cookie; this mount
 * applies it and tracks `prefers-color-scheme` while the choice is System.
 *
 * Skipped on forced-dark surfaces so a visitor's app preference cannot leak
 * onto city hosts or `/admin` (same rule as ThemeProvider in providers.tsx).
 * Hydration, account fallback, and cookie writes are skipped there too.
 * ThemeProvider persistence is off (`storageKey={null}`); this mount hydrates
 * the cookie store, including a one-time localStorage → cookie migration.
 */
export function ThemePreferenceSync({ forcedDark = false }: ThemePreferenceSyncProps): null {
  const surface = useCitySurface();
  const { user } = useAuth();
  const { setMode } = useTheme();
  const skipForcedDark = forcedDark || surface === 'marketing-city';
  const themePreference = useSyncExternalStore(
    subscribeThemePreference,
    getThemePreferenceSnapshot,
    getThemePreference,
  );

  useEffect(() => {
    if (skipForcedDark) return;
    hydrateThemePreferenceFromCookie();
  }, [skipForcedDark]);

  useEffect(() => {
    if (skipForcedDark) return;
    applyAccountThemePreference(user?.preferences?.theme);
  }, [skipForcedDark, user?.preferences?.theme]);

  useEffect(() => {
    if (skipForcedDark) return;
    setMode(resolveThemeMode(themePreference, getSystemPrefersDark()));
  }, [setMode, skipForcedDark, themePreference]);

  useEffect(() => {
    if (skipForcedDark) return;
    if (themePreference !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent): void => {
      setMode(event.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [setMode, skipForcedDark, themePreference]);

  return null;
}
